import Voice from '@react-native-community/voice';
import { RouteProp, useRoute } from '@react-navigation/native';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Image,
  Platform,
  Modal as RNModal,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import {
  CameraOptions,
  ImageLibraryOptions,
  ImagePickerResponse,
  launchCamera,
  launchImageLibrary,
} from 'react-native-image-picker';

import { useAuth } from '../hooks/useAuth';
import { useSocket } from '../hooks/useSocket';
import { RootStackParamList } from '../navigation/types';
import groupService from '../services/group.service';
import messageService from '../services/message.service';
import { useFileUpload } from '../services/upload.service';
import { showToast } from '../utils/toast';

interface ChatScreenMessage {
  _id: string;
  from: string | { id: string; nickname: string; avatar: string };
  content: string;
  type: 'text' | 'image' | 'voice' | 'video';
  groupId?: string;
  readAt?: Date;
  replyTo?: string;
  isRead?: boolean;
  createdAt: Date;
  sender?: {
    id: string;
    nickname: string;
    avatar: string;
  };
  readReceipt?: {
    from: string;
  };
}

type ChatScreenRouteProp = RouteProp<RootStackParamList, 'Chat'>;

export default function ChatScreen() {
  const route = useRoute<ChatScreenRouteProp>();
  const { groupId, userId: _userId } = route.params;

  const { isConnected: connected, on, off, emit } = useSocket();
  const { user } = useAuth();
  const { uploadImage, uploading, error: uploadError } = useFileUpload();

  const [inputText, setInputText] = useState('');
  const [typingUsers, setTypingUsers] = useState<string[]>([]);
  const [messages, setMessages] = useState<ChatScreenMessage[]>([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [showImagePickerModal, setShowImagePickerModal] = useState(false);
  const flatListRef = useRef<FlatList<ChatScreenMessage>>(null);

  // 当前组信息
  const groups = groupService.getAll();
  const currentGroup = groups.find((g: any) => g.id === groupId);
  const isGroupChat = !!groupId;

  // Chat ID for loading messages
  const chatId = groupId || _userId || '';

  // Load messages from service
  const loadMessages = useCallback(async () => {
    if (!chatId) return;

    try {
      setIsLoadingMessages(true);
      const historyMessages = await messageService.loadHistory(chatId);
      setMessages(historyMessages as ChatScreenMessage[]);

      // Scroll to bottom after loading
      setTimeout(() => {
        flatListRef.current?.scrollToEnd({ animated: false });
      }, 100);
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setIsLoadingMessages(false);
    }
  }, [chatId]);

  useEffect(() => {
    // 加载历史消息
    loadMessages();

    // 监听新消息
    const handleMessage = (message: ChatScreenMessage) => {
      const messageGroupId = message.groupId;
      if (messageGroupId === groupId || (!messageGroupId && !isGroupChat)) {
        setMessages((prev) => [...prev, message]);

        // 处理已读回执
        if (message.readReceipt?.from === user?.id) {
          messageService.markAsRead(message.replyTo || '', user?.id || '');
        }

        // Scroll to bottom on new message
        setTimeout(() => {
          flatListRef.current?.scrollToEnd({ animated: true });
        }, 100);
      }
    };

    const handleTyping = ({ from, isTyping }: { from: string; isTyping: boolean }) => {
      if (from !== user?.id) {
        setTypingUsers((prev) => {
          if (isTyping) {
            return prev.includes(from) ? [...prev] : [...prev, from];
          } else {
            return prev.filter((id: string) => id !== from);
          }
        });
      }
    };

    on('message', handleMessage);
    on('typing', handleTyping);

    return () => {
      off('message', handleMessage);
      off('typing', handleTyping);
    };
  }, [on, off, groupId, user, isGroupChat, loadMessages]);

  // Initialize voice recognition
  useEffect(() => {
    Voice.onSpeechStart = () => {
      setIsRecording(true);
    };

    Voice.onSpeechEnd = () => {
      setIsRecording(false);
    };

    Voice.onSpeechResults = (results: any) => {
      if (results.value && results.value[0]) {
        setInputText(results.value[0]);
      }
    };

    Voice.onSpeechError = (error: any) => {
      console.error('Speech recognition error:', error);
      setIsRecording(false);
    };

    return () => {
      Voice.destroy().then(Voice.removeAllListeners);
    };
  }, []);

  const handleSend = useCallback(async () => {
    if (!inputText.trim()) return;

    const message = {
      type: 'text' as const,
      content: inputText,
      groupId,
    };

    try {
      const sentMessage = await messageService.sendMessage(message);
      setMessages((prev) => [...prev, sentMessage as ChatScreenMessage]);
      setInputText('');

      // Scroll to bottom
      setTimeout(() => {
        flatListRef.current?.scrollToEnd({ animated: true });
      }, 100);
    } catch (error) {
      console.error('Failed to send message:', error);
      showToast('消息发送失败，请重试', 'error');
    }
  }, [inputText, groupId]);

  // Image picker implementation
  const handleImagePick = useCallback(() => {
    setShowImagePickerModal(true);
  }, []);

  const handleImageResponse = useCallback(
    async (response: ImagePickerResponse) => {
      if (response.didCancel) {
        return;
      }

      if (response.errorCode) {
        console.error('Image picker error:', response.errorMessage);
        showToast(response.errorMessage || '选择图片失败', 'error');
        return;
      }

      const asset = response.assets?.[0];
      if (!asset?.uri) {
        return;
      }

      try {
        // Upload the image
        const uploadResult = await uploadImage(asset.uri);

        if (uploadResult) {
          // Send image message
          const message = {
            type: 'image' as const,
            content: uploadResult.url,
            groupId,
          };

          const sentMessage = await messageService.sendMessage(message);
          setMessages((prev) => [...prev, sentMessage as ChatScreenMessage]);

          // Scroll to bottom
          setTimeout(() => {
            flatListRef.current?.scrollToEnd({ animated: true });
          }, 100);
        }
      } catch (error) {
        console.error('Failed to upload/send image:', error);
        showToast('图片发送失败，请重试', 'error');
      }
    },
    [uploadImage, groupId],
  );

  const handleTakePhoto = useCallback(() => {
    setShowImagePickerModal(false);
    const options: CameraOptions = {
      mediaType: 'photo',
      quality: 0.8 as any,
      maxWidth: 1920,
      maxHeight: 1080,
      saveToPhotos: true,
    };
    launchCamera(options, handleImageResponse);
  }, [handleImageResponse]);

  const handleChooseFromLibrary = useCallback(() => {
    setShowImagePickerModal(false);
    const options: ImageLibraryOptions = {
      mediaType: 'photo',
      quality: 0.8 as any,
      maxWidth: 1920,
      maxHeight: 1080,
      selectionLimit: 1,
    };
    launchImageLibrary(options, handleImageResponse);
  }, [handleImageResponse]);

  // Voice recording implementation
  const handleVoiceRecord = useCallback(async () => {
    try {
      if (isRecording) {
        // Stop recording
        await Voice.stop();
        setIsRecording(false);
      } else {
        // Start recording
        const hasPermission = await Voice.isAvailable();
        if (!hasPermission) {
          showToast('语音识别不可用', 'error');
          return;
        }

        await Voice.start(Platform.OS === 'ios' ? 'zh-CN' : 'cmn-Hans-CN');
      }
    } catch (error) {
      console.error('Voice recording error:', error);
      setIsRecording(false);
      showToast('语音识别启动失败', 'error');
    }
  }, [isRecording]);

  const renderMessage = ({ item }: { item: ChatScreenMessage }) => {
    const fromId = typeof item.from === 'string' ? item.from : item.from?.id;
    const isMyMessage = fromId === user?.id;
    const isReplyToMe = item.replyTo === user?.id;

    return (
      <View
        style={[
          styles.messageRow,
          isMyMessage ? styles.myMessage : styles.otherMessage,
          isReplyToMe && styles.replyMessage,
        ]}
      >
        {!isMyMessage && item.sender?.avatar && (
          <Image source={{ uri: item.sender.avatar }} style={styles.avatar} />
        )}

        <View
          style={[
            styles.messageContent,
            isMyMessage ? styles.myMessageContent : styles.otherMessageContent,
          ]}
        >
          {item.type === 'text' ? (
            <Text style={[styles.text, isMyMessage && styles.myText]}>{item.content}</Text>
          ) : item.type === 'image' ? (
            <Image source={{ uri: item.content }} style={styles.imageMessage} resizeMode="cover" />
          ) : (
            <Text style={[styles.text, isMyMessage && styles.myText, { fontStyle: 'italic' }]}>
              [{item.type === 'voice' ? '语音消息' : item.type}]
            </Text>
          )}
        </View>

        {item.readAt && isMyMessage && (
          <Text style={styles.readTime}>{new Date(item.readAt).toLocaleTimeString()}</Text>
        )}
      </View>
    );
  };

  const renderTyping = ({ userId }: { userId: string }) => (
    <View key={userId} style={styles.typingIndicator}>
      <View style={styles.typingDots}>
        <View style={[styles.dot, styles.dot1]} />
        <View style={[styles.dot, styles.dot2]} />
        <View style={[styles.dot, styles.dot3]} />
      </View>
      <Text style={styles.typingText}>对方正在输入...</Text>
    </View>
  );

  return (
    <View style={styles.container}>
      {/* 头部 */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => {}}>
          <Image
            source={{ uri: user?.avatar || 'https://via.placeholder.com/50' }}
            style={styles.headerAvatar}
          />
        </TouchableOpacity>

        <View style={styles.headerInfo}>
          <Text style={styles.headerTitle}>
            {isGroupChat ? currentGroup?.name || '群聊' : '单聊'}
          </Text>
          <Text style={styles.headerSubtitle}>
            {isGroupChat ? `${currentGroup?.memberCount || 0}人` : '在线'}
          </Text>
        </View>

        <TouchableOpacity>
          <Text style={styles.headerAction}>⋮</Text>
        </TouchableOpacity>
      </View>

      {/* 在线用户列表 */}
      {isGroupChat && currentGroup?.members && (
        <View style={styles.onlineUsers}>
          <FlatList
            horizontal
            data={currentGroup.members}
            renderItem={({ item }: { item: any }) => (
              <View style={styles.onlineUser}>
                <Image source={{ uri: item.avatar }} style={styles.onlineAvatar} />
                <Text style={styles.onlineName}>{item.nickname}</Text>
              </View>
            )}
            keyExtractor={(item: any) => item.id}
          />
        </View>
      )}

      {/* 消息列表 */}
      {isLoadingMessages ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#007AFF" />
          <Text style={styles.loadingText}>加载消息中...</Text>
        </View>
      ) : (
        <FlatList
          ref={flatListRef}
          data={messages}
          renderItem={renderMessage}
          keyExtractor={(item: ChatScreenMessage) => item._id}
          contentContainerStyle={styles.messagesContainer}
          style={styles.messagesList}
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <Text style={styles.emptyText}>暂无消息</Text>
            </View>
          }
        />
      )}

      {/* 正在输入提示 */}
      {typingUsers.length > 0 && (
        <View style={styles.typingSection}>
          {typingUsers.map((userId) => renderTyping({ userId }))}
        </View>
      )}

      {/* 输入区域 */}
      <View style={styles.inputArea}>
        <View style={styles.inputActions}>
          <TouchableOpacity onPress={handleImagePick} disabled={uploading}>
            <Text style={styles.actionIcon}>{uploading ? '⏳' : '📷'}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={handleVoiceRecord}
            style={isRecording && styles.recordingButton}
          >
            <Text style={styles.actionIcon}>{isRecording ? '🔴' : '🎤'}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => {}}
            disabled={typingUsers.length > 0}
            style={[styles.actionBtn, typingUsers.length > 0 && styles.actionBtnDisabled]}
          >
            <Text style={styles.actionText}>+</Text>
          </TouchableOpacity>
        </View>

        <TextInput
          style={styles.textInput}
          value={inputText}
          onChangeText={setInputText}
          placeholder="输入消息..."
          placeholderTextColor="#999"
          multiline
          returnKeyType="send"
          onSubmitEditing={handleSend}
        />

        <TouchableOpacity
          style={[styles.sendButton, !inputText.trim() && styles.sendButtonDisabled]}
          onPress={handleSend}
          disabled={!inputText.trim()}
        >
          <Text style={styles.sendButtonText}>发送</Text>
        </TouchableOpacity>
      </View>

      {/* 连接状态 */}
      {!connected && (
        <View style={styles.disconnectOverlay}>
          <ActivityIndicator size="large" color="#fff" />
          <Text style={styles.disconnectText}>正在连接...</Text>
        </View>
      )}

      {/* 图片选择 Modal */}
      <RNModal
        visible={showImagePickerModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowImagePickerModal(false)}
      >
        <TouchableOpacity
          style={styles.imagePickerOverlay}
          activeOpacity={1}
          onPress={() => setShowImagePickerModal(false)}
        >
          <View style={styles.imagePickerSheet}>
            <Text style={styles.imagePickerTitle}>选择图片</Text>
            <TouchableOpacity style={styles.imagePickerOption} onPress={handleTakePhoto}>
              <Text style={styles.imagePickerOptionText}>拍照</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.imagePickerOption} onPress={handleChooseFromLibrary}>
              <Text style={styles.imagePickerOptionText}>从相册选择</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.imagePickerOption, styles.imagePickerCancel]}
              onPress={() => setShowImagePickerModal(false)}
            >
              <Text style={[styles.imagePickerOptionText, { color: '#999' }]}>取消</Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </RNModal>
    </View>
  );
}

const styles = StyleSheet.create({
  actionBtn: {
    alignItems: 'center',
    backgroundColor: '#007AFF',
    borderRadius: 16,
    height: 32,
    justifyContent: 'center',
    marginLeft: 4,
    width: 32,
  },
  actionBtnDisabled: {
    backgroundColor: '#ccc',
  },
  actionIcon: {
    fontSize: 24,
    paddingHorizontal: 8,
  },
  actionText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold',
  },
  avatar: {
    borderRadius: 16,
    height: 32,
    marginRight: 8,
    width: 32,
  },
  container: {
    backgroundColor: '#f5f5f5',
    flex: 1,
  },
  disconnectOverlay: {
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',
    bottom: 0,
    justifyContent: 'center',
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  disconnectText: {
    color: '#fff',
    fontSize: 16,
    marginTop: 16,
  },
  dot: {
    borderRadius: 4,
    height: 8,
    marginHorizontal: 2,
    width: 8,
  },
  dot1: {
    backgroundColor: '#007AFF',
  },
  dot2: {
    backgroundColor: '#007AFF',
    opacity: 0.7,
  },
  dot3: {
    backgroundColor: '#007AFF',
    opacity: 0.4,
  },
  emptyContainer: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    paddingVertical: 100,
  },
  emptyText: {
    color: '#999',
    fontSize: 16,
  },
  header: {
    alignItems: 'center',
    backgroundColor: '#fff',
    borderBottomColor: '#e5e5e5',
    borderBottomWidth: 1,
    flexDirection: 'row',
    padding: 16,
    paddingTop: 50,
  },
  headerAction: {
    color: '#333',
    fontSize: 20,
  },
  headerAvatar: {
    borderRadius: 22,
    height: 44,
    marginRight: 12,
    width: 44,
  },
  headerInfo: {
    flex: 1,
    marginLeft: 8,
  },
  headerSubtitle: {
    color: '#999',
    fontSize: 14,
    marginTop: 2,
  },
  headerTitle: {
    color: '#333',
    fontSize: 18,
    fontWeight: 'bold',
  },
  imageMessage: {
    borderRadius: 12,
    height: 200,
    width: 200,
  },
  imagePickerCancel: {
    borderBottomWidth: 0,
    marginTop: 8,
  },
  imagePickerOption: {
    borderBottomColor: '#f0f0f0',
    borderBottomWidth: 1,
    paddingVertical: 16,
  },
  imagePickerOptionText: {
    color: '#333',
    fontSize: 16,
    textAlign: 'center',
  },
  imagePickerOverlay: {
    backgroundColor: 'rgba(0,0,0,0.4)',
    flex: 1,
    justifyContent: 'flex-end',
  },
  imagePickerSheet: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    paddingBottom: 34,
    paddingHorizontal: 20,
  },
  imagePickerTitle: {
    borderBottomColor: '#f0f0f0',
    borderBottomWidth: 1,
    color: '#333',
    fontSize: 16,
    fontWeight: '600',
    paddingVertical: 16,
    textAlign: 'center',
  },
  inputActions: {
    alignItems: 'center',
    flexDirection: 'row',
    marginRight: 8,
  },
  inputArea: {
    alignItems: 'flex-end',
    backgroundColor: '#fff',
    borderTopColor: '#e5e5e5',
    borderTopWidth: 1,
    flexDirection: 'row',
    padding: 12,
  },
  loadingContainer: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  loadingText: {
    color: '#999',
    fontSize: 16,
    marginTop: 12,
  },
  messageContent: {
    borderRadius: 16,
    maxWidth: '70%',
    padding: 12,
  },
  messageRow: {
    alignItems: 'flex-end',
    flexDirection: 'row',
    marginBottom: 12,
  },
  messagesContainer: {
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  messagesList: {
    flex: 1,
  },
  myMessage: {
    alignSelf: 'flex-end',
    flexDirection: 'row-reverse',
  },
  myMessageContent: {
    backgroundColor: '#007AFF',
    borderBottomRightRadius: 4,
  },
  myText: {
    color: '#fff',
  },
  onlineAvatar: {
    borderRadius: 18,
    height: 36,
    marginBottom: 4,
    width: 36,
  },
  onlineName: {
    color: '#666',
    fontSize: 12,
  },
  onlineUser: {
    alignItems: 'center',
    marginRight: 12,
  },
  onlineUsers: {
    backgroundColor: '#fff',
    flexDirection: 'row',
    paddingLeft: 16,
    paddingVertical: 8,
  },
  otherMessage: {
    alignSelf: 'flex-start',
  },
  otherMessageContent: {
    backgroundColor: '#fff',
    borderBottomLeftRadius: 4,
  },
  readTime: {
    color: '#999',
    fontSize: 10,
    marginLeft: 8,
    marginTop: 4,
  },
  recordingButton: {
    backgroundColor: '#ff4d4f20',
    borderRadius: 20,
  },
  replyMessage: {
    backgroundColor: '#fffbe6',
    borderRadius: 12,
    padding: 4,
  },
  sendButton: {
    backgroundColor: '#007AFF',
    borderRadius: 20,
    marginLeft: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  sendButtonDisabled: {
    backgroundColor: '#ccc',
  },
  sendButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  text: {
    color: '#333',
    fontSize: 15,
    lineHeight: 20,
  },
  textInput: {
    backgroundColor: '#f5f5f5',
    borderRadius: 20,
    color: '#333',
    flex: 1,
    fontSize: 16,
    maxHeight: 100,
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  typingDots: {
    flexDirection: 'row',
  },
  typingIndicator: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  typingSection: {
    backgroundColor: '#fff',
    padding: 12,
  },
  typingText: {
    color: '#999',
    fontSize: 12,
    marginLeft: 12,
  },
});
